import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { MultiSelect } from '@mantine/core'
import { formatHumanName } from '@medplum/core'
import { useMedplumProfile } from '@medplum/react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useCareCoordinatorRole } from '@/hooks/useCareCoordinatorRole'
import { useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import { DayScheduleView, Encounters, Messages, OverviewCalendar, Results } from './components'
import { TodaysBriefing } from './components/Briefing/TodaysBriefing'
import { fetchScheduleEncounters, type ScheduleEncounter } from './service'

const DATE_FORMAT = 'YYYY-MM-DD'

export const HomePage = () => {
  const profile = useMedplumProfile()
  const middleware = useMiddlewareClient()
  const { isCareCoordinator, practitioners, status: roleStatus } = useCareCoordinatorRole()

  // Only an explicit pick is remembered. Leaving the default untouched keeps it
  // relative to the current day, so returning after midnight opens the new today
  // instead of the date on which Home was first mounted (PD-1206).
  const [rememberedSelectedDate, setSelectedDate] = useSessionState<string | null>(
    'home:selectedDate',
    null,
  )
  const selectedDate = rememberedSelectedDate ?? dayjs().format(DATE_FORMAT)
  // Derived from the restored day, not from today. The encounter fetch below is
  // keyed on `visibleMonth` alone, so defaulting to the current month after a
  // remount would load August's encounters while the restored day is in July —
  // the day panel would report "no appointments" from data it never requested.
  const [visibleMonth, setVisibleMonth] = useState<string>(
    dayjs(selectedDate).startOf('month').format(DATE_FORMAT),
  )
  const [encounters, setEncounters] = useState<ScheduleEncounter[]>([])
  // Kept for the session alongside the section filters. Without it the scope
  // silently widens back to every practitioner while the restored filter chips
  // stay lit, so the row would describe a different population than it did
  // (PD-1206).
  const [selectedPractitionerIds, setSelectedPractitionerIds] = useSessionState<string[]>(
    'home:selectedPractitionerIds',
    [],
  )

  const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined

  const effectivePractitionerIds = useMemo(() => {
    if (isCareCoordinator) {
      return selectedPractitionerIds.length > 0
        ? selectedPractitionerIds
        : practitioners.map((p) => p.id).filter((id): id is string => Boolean(id))
    }
    return practitionerId ? [practitionerId] : []
  }, [isCareCoordinator, selectedPractitionerIds, practitioners, practitionerId])

  const resultsPractitionerIds = useMemo(() => {
    if (isCareCoordinator && selectedPractitionerIds.length > 0) {
      return selectedPractitionerIds
    }
    return undefined
  }, [isCareCoordinator, selectedPractitionerIds])

  const practitionerOptions = useMemo(
    () =>
      practitioners.map((p) => ({
        value: p.id as string,
        label: formatHumanName(p.name?.[0]) || p.id || 'Unknown',
      })),
    [practitioners],
  )

  useEffect(() => {
    if (!effectivePractitionerIds.length) return

    let cancelled = false
    const monthStart = dayjs(visibleMonth).startOf('month')
    const dateFrom = monthStart.startOf('day').toISOString()
    const dateTo = monthStart.endOf('month').endOf('day').toISOString()

    fetchScheduleEncounters(middleware, {
      dateFrom,
      dateTo,
      practitionerIds: effectivePractitionerIds,
    })
      .then((response) => {
        if (cancelled) return
        setEncounters(response.encounters)
      })
      .catch((err) => {
        if (cancelled) return
        logError('home', 'Failed to fetch schedule encounters', err)
      })

    return () => {
      cancelled = true
    }
  }, [middleware, visibleMonth, effectivePractitionerIds])

  return (
    <div className="h-[calc(100vh-64px)] overflow-hidden bg-gray-100 p-4">
      <div className="flex h-full gap-4">
        <div className="flex w-[380px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {isCareCoordinator && (
            <div className="shrink-0 border-b border-gray-100 px-3 py-2">
              <MultiSelect
                placeholder={selectedPractitionerIds.length === 0 ? 'All practitioners' : undefined}
                data={practitionerOptions}
                value={selectedPractitionerIds}
                onChange={setSelectedPractitionerIds}
                searchable
                clearable
                size="xs"
                maxValues={20}
              />
            </div>
          )}
          <OverviewCalendar
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            visibleMonth={visibleMonth}
            onVisibleMonthChange={setVisibleMonth}
            encounters={encounters}
          />
          <div className="flex-1 overflow-auto border-t border-gray-200">
            <DayScheduleView selectedDate={selectedDate} encounters={encounters} />
          </div>
        </div>
        <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
            <h2 className="text-lg font-bold text-gray-900">Clinical Inbox</h2>
            <span className="text-sm text-gray-400">{dayjs().format('dddd, MMMM D, YYYY')}</span>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-6">
            {/* Providers only: care coordinators keep the Care Queue (PD-1936). */}
            {practitionerId && roleStatus === 'ready' && !isCareCoordinator && (
              <div className="pt-4">
                <TodaysBriefing />
              </div>
            )}
            <div className="pt-4">
              <Messages />
            </div>
            <p className="pt-5 pb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
              Encounters
            </p>
            <Encounters practitionerIds={effectivePractitionerIds} />
            <p className="pt-5 pb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
              Results
            </p>
            <Results practitionerIds={resultsPractitionerIds} />
          </div>
        </div>
      </div>
    </div>
  )
}
